(() => {
  if (customElements.get('rdb-packaging-options')) return;

  class RdbPackagingOptions extends HTMLElement {
    static observedAttributes = ['data-product-id', 'data-packaging-family'];

    #controller;
    #observer;
    #expanded = false;
    #identity = '';

    connectedCallback() {
      this.#controller?.abort();
      this.#observer?.disconnect();
      this.#controller = new AbortController();
      const { signal } = this.#controller;
      this.addEventListener('change', this.#onChange, { signal });
      this.addEventListener('click', this.#onClick, { signal });
      this.closest('form')?.addEventListener('reset', this.#onReset, { signal });
      this.#observer = new MutationObserver(this.#onChildrenChanged);
      this.#observer.observe(this, { childList: true, subtree: true });
      queueMicrotask(() => {
        if (this.isConnected) this.#refresh();
      });
    }

    disconnectedCallback() {
      this.#controller?.abort();
      this.#observer?.disconnect();
    }

    attributeChangedCallback() {
      if (!this.isConnected) return;
      queueMicrotask(() => {
        if (this.isConnected) this.#refresh();
      });
    }

    #onChange = (event) => {
      if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-packaging-colour]')) return;
      this.#refresh();
    };

    #onReset = (event) => {
      queueMicrotask(() => {
        if (!this.isConnected || event.defaultPrevented) return;
        this.#expanded = false;
        this.#refresh();
      });
    };

    #onChildrenChanged = (records) => {
      const selector = '.rdb-packaging__fieldset, .rdb-packaging__grid, .rdb-packaging__option, [data-packaging-toggle], [data-packaging-colour]';
      const packagingChanged = records.some((record) =>
        [...record.addedNodes, ...record.removedNodes].some((node) =>
          node instanceof Element && (node.matches(selector) || node.querySelector(selector))
        )
      );
      if (packagingChanged && this.isConnected) this.#refresh();
    };

    #onClick = (event) => {
      if (!(event.target instanceof Element)) return;
      const toggle = event.target.closest('[data-packaging-toggle]');
      if (!toggle || !this.contains(toggle)) return;
      const expanding = !this.#expanded;
      this.#expanded = expanding;
      this.#refresh();
      if (expanding && event.detail === 0) {
        this.querySelector('[data-packaging-extra]:not([hidden]) input')?.focus();
      }
    };

    #refresh() {
      const identity = `${this.dataset.productId}:${this.dataset.packagingFamily}`;
      if (identity !== this.#identity) {
        this.#identity = identity;
        this.#expanded = false;
      }

      const extras = Array.from(this.querySelectorAll('[data-packaging-extra]'));
      const toggle = this.querySelector('[data-packaging-toggle]');
      if (!extras.length || !(toggle instanceof HTMLButtonElement)) return;

      const selectedExtra = extras.some((option) => option.querySelector('input:checked'));
      if (selectedExtra) this.#expanded = true;
      extras.forEach((option) => { option.hidden = !this.#expanded; });
      toggle.hidden = selectedExtra;
      toggle.setAttribute('aria-expanded', String(this.#expanded));

      const label = toggle.querySelector('[data-packaging-toggle-label]');
      if (label) label.textContent = this.#expanded ? toggle.dataset.lessLabel : toggle.dataset.moreLabel;
    }
  }

  customElements.define('rdb-packaging-options', RdbPackagingOptions);
})();
